import { addDays, addMonths, todayIso, stepTime } from './editors.js';
import { NIR, CHECKISH, normalizeKeyCombo, keyComboOf } from './grid-util.js';
import { RowState } from './dataset.js';
export class GridKeys {
    // ═══════════════════════ 키보드 ═══════════════════════
    /** @internal */ onKeyDown(e) {
        if (e.isComposing || e.keyCode === 229) {
            // 한글 조합 중 Enter/Tab: 조합 확정 후 이어서 처리
            if (this.editing && (e.key === 'Enter' || e.key === 'Tab')) {
                this.pendingKey = e;
                if (e.key === 'Tab')
                    e.preventDefault();
            }
            return;
        }
        if (e.key !== 'Escape' && e.key !== 'Shift' && e.key !== 'Control' && e.key !== 'Alt' && e.key !== 'Meta')
            this.escArm = -1; // Esc 두 번 되돌리기: 사이에 다른 키를 누르면 처음부터
        if (this.opts.onKeyDown?.(e, this) === true) {
            e.preventDefault();
            return;
        }
        if (this.opts.keyMap) {
            if (this.keyMapSrc !== this.opts.keyMap) {
                this.keyMapSrc = this.opts.keyMap;
                this.keyMapN = new Map(Object.entries(this.opts.keyMap).map(([k, v]) => [normalizeKeyCombo(k), v]));
            }
            const a = this.keyMapN.get(keyComboOf(e));
            if (a !== undefined) {
                e.preventDefault();
                e.stopPropagation();
                if (a !== false)
                    this.runKeyAction(a, e);
                return;
            }
        }
        if ((e.ctrlKey || e.metaKey) && (e.key === 'f' || e.key === 'F') && this.opts.findPanel !== false) {
            e.preventDefault();
            this.openFind();
            return;
        }
        if ((e.ctrlKey || e.metaKey) && (e.key === 'h' || e.key === 'H') && this.opts.editable && !this.editing) {
            e.preventDefault();
            this.openReplace();
            return;
        }
        if (e.altKey && e.key === 'ArrowDown' && !this.editing) { // 헤더 필터 메뉴를 키보드로
            const c = this.cols[this.focus.ci];
            if (c && this.menuFilterable(c) && this.opts.columnFilterMenu !== false) {
                e.preventDefault();
                this.openFilterMenu(c.id);
                return;
            }
        }
        // Alt+Shift+→: 다른 그리드로 보내기 (⋮⋮ 끌어 놓기의 키보드 대안)
        if (e.altKey && e.shiftKey && !e.ctrlKey && !e.metaKey && e.key === 'ArrowRight' && this.opts.dragSource && !this.editing) {
            e.preventDefault();
            this.sendRows();
            return;
        }
        if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'u' || e.key === 'U') && !this.editing) {
            const row = this.getFocusedRow(), c = this.cols[this.focus.ci];
            if (row >= 0 && c?.def.privacy && c.def.unmask) {
                e.preventDefault();
                this.toggleUnmask(row, c.id);
                return;
            }
        }
        if (e.key === 'F3' && this.findMatches.length) {
            e.preventDefault();
            this.findNext(e.shiftKey ? -1 : 1);
            return;
        }
        if (!this.editing && this.incText && this.handleIncKey(e)) {
            e.preventDefault();
            return;
        }
        if (this.editing) {
            if (this.handleEditKey(e))
                e.preventDefault();
            return;
        }
        if (this.handleNavKey(e))
            e.preventDefault();
    }
    /** keyMap 동작 실행 (코드에서 직접 불러도 됨) */
    runKeyAction(a, e) {
        if (typeof a === 'function') {
            a(this, e);
            return;
        }
        const commit = () => !this.editing || this.commitEdit();
        const { vi, ci } = this.focus;
        switch (a) {
            case 'nextCell':
                if (commit())
                    this.moveTab(false);
                break;
            case 'prevCell':
                if (commit())
                    this.moveTab(true);
                break;
            case 'nextRow':
            case 'commitAndDown':
                if (commit())
                    this.vmove(1);
                break;
            case 'prevRow':
                if (commit())
                    this.vmove(-1);
                break;
            case 'commitAndRight':
                if (commit())
                    this.setFocus(vi, ci + 1, false);
                break;
            case 'firstCell':
                if (commit())
                    this.setFocus(0, this.firstDataCol(), false);
                break;
            case 'lastCell':
                if (commit())
                    this.setFocus(this.disp.length - 1, this.cols.length - 1, false);
                break;
            case 'beginEdit':
                this.beginEdit('full');
                break;
            case 'cancelEdit':
                this.cancelEdit();
                break;
            case 'addRow':
                if (commit() && this.opts.editable)
                    this.addRow(undefined, true, 'insert');
                break;
            case 'deleteRow':
                if (this.opts.editable) {
                    this.cancelEdit();
                    void this.deleteRows(undefined, { source: 'key' });
                }
                break;
            case 'fillDown':
                this.fillDown();
                break;
            case 'fillRight':
                this.fillRight();
                break;
            case 'restoreRow':
                this.restoreRows();
                break;
            case 'saveRow':
                if (commit())
                    this.saveRowEdit();
                break;
            case 'cancelRow':
                this.cancelRowEdit();
                break;
            case 'undo':
                this.cancelEdit();
                this.undo();
                break;
            case 'redo':
                this.cancelEdit();
                this.redo();
                break;
            case 'save':
                if (commit())
                    this.opts.onSave?.(this);
                break;
            case 'find':
                this.openFind();
                break;
            case 'replace':
                if (this.opts.editable)
                    this.openReplace();
                break;
            case 'copy':
                this.ime.focus();
                document.execCommand('copy');
                break;
            case 'sendRows':
                if (commit())
                    this.sendRows();
                break;
            case 'none': break;
        }
    }
    /** 편집 중 키. 처리했으면 true */
    /** @internal */ handleEditKey(e) {
        const ed = this.editing;
        if (!ed)
            return this.handleNavKey(e);
        if (ed.kind === 'checkCombo' && this.handleCheckComboKey(e))
            return true;
        const dropOpen = !this.drop.hidden && (ed.kind === 'combo' || (ed.kind === 'text' && !!this.cols[ed.ci]?.def.autoComplete));
        // 바로 입력 중 F2 → 전체 편집 (엑셀: 입력 모드 ↔ 편집 모드). 화살표가 칸 이동 대신 글자·달력 이동
        if (e.key === 'F2' && !e.altKey && !e.ctrlKey && !e.shiftKey) {
            if (ed.mode === 'quick')
                ed.mode = 'full';
            return true;
        }
        // 달력: ←→ 하루, ↑↓ 한 주 (F2·더블클릭 편집, 또는 calendarArrowKeys: 'always'), PgUp/PgDn 한 달
        if (ed.kind === 'date' && !this.cal.hidden && !e.altKey && !e.ctrlKey) {
            const step = e.key === 'PageUp' || e.key === 'PageDown' ? 0 : ed.mode !== 'full' && this.opts.calendarArrowKeys !== 'always' ? null : e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : e.key === 'ArrowUp' ? -7 : e.key === 'ArrowDown' ? 7 : null;
            if (step != null) {
                const base = this.calCur || todayIso(), dc = this.cols[ed.ci];
                this.calCur = this.clampDate(dc, ed.row, step === 0 ? addMonths(base, e.key === 'PageDown' ? 1 : -1) : addDays(base, step));
                this.ime.value = dc.def.dateKind === 'datetime' ? this.calCur + ' ' + this.calTime() : this.calCur;
                this.ime.setSelectionRange(0, this.calCur.length);
                this.renderCal();
                return true;
            }
        }
        // 스핀: ↑↓ 증감 (바로 입력·전체 편집 모두)
        if (ed.kind === 'spin' && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
            this.spinBy(e.key === 'ArrowUp' ? 1 : -1, e.shiftKey);
            return true;
        }
        // 시간: ↑↓ 분 단위 증감 (F2·더블클릭 편집일 때)
        if (ed.kind === 'time' && ed.mode === 'full' && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
            const st = (this.cols[ed.ci].def.timeStep ?? 1) * (e.shiftKey ? 10 : 1);
            this.ime.value = stepTime(this.ime.value, e.key === 'ArrowUp' ? st : -st);
            this.ime.select();
            return true;
        }
        if (ed.kind === 'textarea' && ed.mode === 'full' && (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight'))
            return false;
        if (e.key === 'F4' && ed.kind === 'combo') {
            if (this.drop.hidden)
                this.openDrop(this.cols[ed.ci], ed.mode === 'full' ? '' : this.ime.value);
            else
                this.drop.hidden = true;
            return true;
        }
        switch (e.key) {
            case 'Escape':
                this.cancelEdit();
                if (this.opts.escRevertsRow)
                    this.escArm = ed.row;
                return true;
            case 'Enter':
                if (ed.kind === 'textarea' && e.altKey) {
                    const i = this.ime;
                    i.setRangeText('\n', i.selectionStart, i.selectionEnd, 'end');
                    return true;
                }
                // Ctrl+Enter: 여러 칸을 골랐으면 그 칸 모두에 (엑셀), 행 편집 중이면 행 저장
                if ((e.ctrlKey || e.metaKey) && !e.altKey && ed.vi !== NIR && this.multiCellSel()) {
                    this.commitToSelection();
                    return true;
                }
                if ((e.ctrlKey || e.metaKey) && this.rowEdit) {
                    if (this.commitEdit())
                        this.saveRowEdit();
                    return true;
                }
                if (!this.commitEdit())
                    return true;
                this.moveAfterEnter(e.shiftKey);
                return true;
            case 'Tab':
                if (!this.commitEdit())
                    return true;
                this.moveTab(e.shiftKey);
                return true;
            case 'ArrowDown':
            case 'ArrowUp':
                if (dropOpen) {
                    this.dropIdx = Math.max(0, Math.min(this.dropItems.length - 1, this.dropIdx + (e.key === 'ArrowDown' ? 1 : -1)));
                    this.renderDrop();
                    return true;
                }
                if (e.altKey && e.key === 'ArrowDown' && ed.kind === 'combo') {
                    this.openDrop(this.cols[ed.ci], ed.mode === 'full' ? '' : this.ime.value);
                    return true;
                } // 목록 열기 (immediateDropDown false)
                if (!this.commitEdit())
                    return true;
                this.vmove(e.key === 'ArrowDown' ? 1 : -1, false);
                return true;
            case 'ArrowLeft':
            case 'ArrowRight':
                if (ed.mode === 'quick' && ed.kind !== 'combo' && ed.kind !== 'checkCombo') {
                    if (!this.commitEdit())
                        return true;
                    this.setFocus(this.focus.vi, this.focus.ci + (e.key === 'ArrowRight' ? 1 : -1), false);
                    return true;
                }
                return false;
            case 'PageDown':
            case 'PageUp':
                if (dropOpen) {
                    this.dropIdx = Math.max(0, Math.min(this.dropItems.length - 1, this.dropIdx + (e.key === 'PageDown' ? 8 : -8)));
                    this.renderDrop();
                    return true;
                }
                return false;
        }
        return false;
    }
    /** @internal */ moveAfterEnter(back) {
        if (this.focus.vi === NIR) {
            if (this.opts.enterKey === 'down' && !back)
                this.postNewItemRow();
            else
                this.nirTab(back);
            return;
        }
        if (this.opts.enterKey === 'down') {
            this.vmove(back ? -1 : 1);
            return;
        }
        this.moveTab(back);
    }
    /** @internal */ moveTab(back) {
        if (this.focus.vi === NIR) {
            this.nirTab(back);
            return;
        }
        if (this.opts.editable) {
            const nx = this.nextEditable(this.focus.vi, this.focus.ci, back ? -1 : 1);
            // 행 편집: 행의 마지막 칸에서 Enter·Tab = 그 행 저장 (실패면 머묾)
            if (this.rowEdit && (!nx || this.disp[nx.vi] !== this.rowEdit.row) && !this.saveRowEdit())
                return;
            if (nx) {
                this.setFocus(nx.vi, nx.ci, false);
                return;
            }
            if (!back && this.opts.appendRowOnTab) {
                this.addRow(undefined, true, 'tab');
                return;
            }
            return;
        }
        const ci = this.stepCol(this.focus.ci, back ? -1 : 1), o = this.navOrd;
        if (ci >= this.cols.length)
            this.setFocus(this.focus.vi + 1, o ? o[0] : 0, false);
        else if (ci < 0)
            this.setFocus(this.focus.vi - 1, o ? o[o.length - 1] : this.cols.length - 1, false);
        else
            this.setFocus(this.focus.vi, ci, false);
    }
    /** 멀티로우: 위·아래 줄의 셀로 (가로로 겹치는 셀). 레코드 끝이면 이웃 레코드의 첫/마지막 줄로 */
    /** @internal */ lineMove(dir, ext) {
        const c = this.cols[this.focus.ci];
        if (!c?.lh)
            return false;
        const line = dir > 0 ? c.lt + c.lh : c.lt - 1;
        const pick = (ln) => {
            const mid = c.gx + c.width / 2; // 패인과 무관한 레이아웃 가로 위치로 비교
            let best = null, bd = Infinity;
            for (const x of this.cols)
                if (x.lh && x.lt <= ln && ln < x.lt + x.lh) {
                    const d = mid >= x.gx && mid < x.gx + x.width ? 0 : Math.min(Math.abs(mid - x.gx), Math.abs(mid - x.gx - x.width));
                    if (d < bd) {
                        bd = d;
                        best = x;
                    }
                }
            return best;
        };
        if (line >= 0 && line < this.lines) {
            const t = pick(line);
            if (t) {
                this.setFocus(this.focus.vi, t.ci, ext);
                return true;
            }
        }
        const t = pick(dir > 0 ? 0 : this.lines - 1);
        if (t) {
            this.setFocus(this.focus.vi + dir, t.ci, ext);
            return true;
        }
        return false;
    }
    /** 멀티로우 ←→: 같은 줄에 걸친 셀 중 바로 옆(레이아웃 가로 위치), 없으면 읽는 순서로 */
    /** @internal */ sideMove(dir, ext) {
        const c = this.cols[this.focus.ci];
        if (!this.navOrd || !c?.lh)
            return false;
        let best = null;
        for (const x of this.cols) {
            if (!x.lh || x === c || x.lt >= c.lt + c.lh || x.lt + x.lh <= c.lt)
                continue;
            if (dir > 0 ? x.gx < c.gx + c.width - 1 : x.gx + x.width > c.gx + 1)
                continue;
            if (!best || (dir > 0 ? x.gx < best.gx : x.gx > best.gx) || (x.gx === best.gx && Math.abs(x.lt - c.lt) < Math.abs(best.lt - c.lt)))
                best = x;
        }
        const ci = best ? best.ci : this.stepCol(c.ci, dir);
        if (ci >= 0 && ci < this.cols.length)
            this.setFocus(this.focus.vi, ci, ext);
        return true;
    }
    /** 읽는 순서로 다음/이전 컬럼 (멀티로우는 줄·가로 위치 순, 끝을 넘으면 -1 또는 cols.length) */
    /** @internal */ stepCol(ci, d) {
        const o = this.navOrd;
        if (!o)
            return ci + d;
        const p = o.indexOf(ci) + d;
        return p < 0 ? -1 : p >= o.length ? this.cols.length : o[p];
    }
    /** @internal */ handleNavKey(e) {
        const { vi, ci } = this.focus;
        if (vi === NIR)
            return this.handleNirKey(e);
        const page = Math.max(1, Math.floor(this.bodyPanes[1].clientHeight / this.rh) - 1);
        const ext = e.shiftKey;
        const ctrl = e.ctrlKey || e.metaKey;
        const no = this.nirOpt();
        // 새 항목 행으로: 위(top) 첫 행에서 ↑·Ctrl+Home, 아래(bottom) 마지막 행에서 ↓·Ctrl+End
        if (no && !ext && !this.disp.length && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
            this.setFocus(NIR, ci, false);
            return true;
        }
        if (no && ctrl && !ext && ((e.key === 'Home' && no.position === 'top') || (e.key === 'End' && no.position === 'bottom'))) {
            this.setFocus(NIR, ci, false);
            return true;
        }
        switch (e.key) {
            case 'ArrowDown':
                if (ctrl && !ext && this.opts.rowMoveKeys) {
                    this.moveSelectedRows(1);
                    return true;
                }
                if (!ctrl && this.lineMove(1, ext))
                    return true;
                if (ctrl)
                    this.setFocus(this.disp.length - 1, ci, ext);
                else
                    this.vmove(1, ext);
                return true;
            case 'ArrowUp':
                if (ctrl && !ext && this.opts.rowMoveKeys) {
                    this.moveSelectedRows(-1);
                    return true;
                }
                if (!ctrl && this.lineMove(-1, ext))
                    return true;
                if (ctrl)
                    this.setFocus(0, ci, ext);
                else
                    this.vmove(-1, ext);
                return true;
            case 'ArrowRight':
                if (this.treeKey(true))
                    return true;
                if (!ctrl && this.sideMove(1, ext))
                    return true;
                this.setFocus(vi, ctrl ? this.cols.length - 1 : ci + 1, ext);
                return true;
            case 'ArrowLeft':
                if (this.treeKey(false))
                    return true;
                if (!ctrl && this.sideMove(-1, ext))
                    return true;
                this.setFocus(vi, ctrl ? 0 : ci - 1, ext);
                return true;
            case 'PageDown':
                this.setFocus(vi + page, ci, ext);
                return true;
            case 'PageUp':
                this.setFocus(vi - page, ci, ext);
                return true;
            case 'Home':
                this.setFocus(ctrl ? 0 : vi, ctrl ? ci : 0, ext);
                return true;
            case 'End':
                this.setFocus(ctrl ? this.disp.length - 1 : vi, ctrl ? ci : this.cols.length - 1, ext);
                return true;
            case 'Tab':
                this.moveTab(e.shiftKey);
                return true;
            case 'Enter': {
                const row = this.getFocusedRow();
                if (row < 0) {
                    this.toggleGroupAt(vi);
                    return true;
                }
                if (ctrl && this.rowEdit) {
                    this.saveRowEdit();
                    return true;
                } // 행 편집 저장
                if (this.opts.editable && !ctrl && this.formMode() && !e.shiftKey) {
                    this.beginEdit('full');
                    return true;
                } // 편집 폼 방식: Enter = 폼 열기
                if (this.opts.editable && !ctrl) {
                    this.moveAfterEnter(e.shiftKey);
                    return true;
                }
                return false;
            }
            case 'F2':
                this.beginEdit('full');
                return true;
            case 'F4': {
                const c = this.cols[this.focus.ci], r = this.getFocusedRow();
                if (c && r >= 0 && this.editorOf(c, r) === 'button') {
                    void this.openButtonEdit(r, c);
                    return true;
                }
                return false;
            }
            case ' ': {
                const c = this.cols[ci], row = this.getFocusedRow();
                if (row < 0) {
                    this.toggleGroupAt(vi);
                    return true;
                }
                if (c?.special === 'check') {
                    this.toggleCheckRow(row);
                    return true;
                }
                if (c?.special === 'detail') {
                    this.toggleDetail(row);
                    return true;
                }
                if (c?.def.onCellClick && (c.def.cellType === 'button' || c.def.cellType === 'link')) {
                    c.def.onCellClick(row, c.id, this);
                    return true;
                }
                if (c && CHECKISH.has(this.editorOf(c, row)) && this.canEditCol(c, row)) {
                    this.toggleCheckCell(c, row);
                    return true;
                }
                return false;
            }
            case 'Delete': {
                // 삭제 행 표시 중 삭제 행에서 Ctrl+Delete = 삭제 취소
                if (ctrl && this.opts.editable && this.opts.showDeletedRows && this.ds.rowState(this.getFocusedRow()) === RowState.DELETE) {
                    this.restoreRows();
                    return true;
                }
                if (ctrl && this.opts.editable) {
                    void this.deleteRows(undefined, { source: 'key' });
                    return true;
                }
                this.clearRange();
                return true;
            }
            case 'Backspace':
                if (this.beginEdit('quick')) {
                    this.ime.value = '';
                    return true;
                }
                return false;
            case 'Insert':
                if (this.opts.editable) {
                    this.addRow(undefined, true, 'insert');
                    return true;
                }
                return false;
            case 'Escape': {
                if (this.rowEdit) {
                    this.cancelRowEdit();
                    return true;
                } // 행 편집 취소 (편집기 Esc 다음 두 번째 Esc)
                const row = this.getFocusedRow();
                if (this.opts.escRevertsRow && this.opts.editable && row >= 0) {
                    if (this.escArm === row) {
                        this.escArm = -1;
                        if (this.revertRowChanges(row)) {
                            this.showTip(this.t('행 변경을 되돌렸습니다'), 'info');
                            setTimeout(() => this.hideTip(), 1500);
                        }
                        return true;
                    }
                    this.escArm = row;
                }
                this.anchor = { ...this.focus };
                this.bump();
                return true;
            }
        }
        // Ctrl+D 아래로 채우기·Ctrl+R 오른쪽으로 채우기 (엑셀)
        if (ctrl && !e.shiftKey && !e.altKey && (e.key === 'd' || e.key === 'D') && this.opts.editable) {
            this.fillDown();
            return true;
        }
        if (ctrl && !e.shiftKey && !e.altKey && (e.key === 'r' || e.key === 'R') && this.opts.editable) {
            this.fillRight();
            return true;
        }
        if (ctrl && e.shiftKey && (e.key === 'c' || e.key === 'C')) {
            this.copyWithHeaders();
            return true;
        }
        if (ctrl && (e.key === 'a' || e.key === 'A')) {
            this.extraRanges = [];
            this.anchor = { vi: 0, ci: 0 };
            this.focus = { vi: this.disp.length - 1, ci: this.cols.length - 1 };
            this.bump();
            return true;
        }
        if (ctrl && (e.key === 'y' || e.key === 'Y' || (e.shiftKey && (e.key === 'z' || e.key === 'Z')))) {
            this.redo();
            return true;
        }
        if (ctrl && (e.key === 'z' || e.key === 'Z')) {
            this.undo();
            return true;
        }
        return false;
    }
}
